import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import { useMemo } from 'react';
import { LegendItem } from './components/LegendItem';
import { NoDataOrLoadingState } from './components/NoDataOrLoadingState';
import { SideChart } from './components/SideChart';
import {
  CENTER_LABEL_WIDTH,
  DEFAULT_HEIGHT,
  DEFAULT_LEFT_COLOR,
  DEFAULT_RIGHT_COLOR,
  X_AXIS_HEIGHT,
} from './constants';
import { defaultValueFormatter, hasRenderableData, resolveColor, toChartValue } from './helper';
import type { PyramidDiagramProps, PyramidDiagramRow } from './types';
import { getDomainAndTicks } from './utils/getDomainAndTicks';

export const PyramidDiagram = ({
  title,
  left,
  right,
  rows,
  metaStats,
  xAxisTicks,
  valueFormatter = defaultValueFormatter,
  isLoading = false,
  isAnimationActive = true,
  containerSx,
  height = DEFAULT_HEIGHT,
}: PyramidDiagramProps) => {
  const leftColor = resolveColor(left.color, DEFAULT_LEFT_COLOR);
  const rightColor = resolveColor(right.color, DEFAULT_RIGHT_COLOR);

  const chartData = useMemo<PyramidDiagramRow[]>(
    () =>
      rows.map(row => ({
        label: row.label,
        leftValue: toChartValue(row.leftValue),
        rightValue: toChartValue(row.rightValue),
      })),
    [rows]
  );

  const rawMax = useMemo(
    () => chartData.reduce((acc, row) => Math.max(acc, row.leftValue, row.rightValue), 0),
    [chartData]
  );

  const { domainMax, ticks } = useMemo(() => getDomainAndTicks(rawMax, xAxisTicks), [rawMax, xAxisTicks]);

  const showChart = !isLoading && hasRenderableData(rows);
  const labelsColumnHeight = height - X_AXIS_HEIGHT;

  return (
    <Box
      data-test="pyramid-diagram"
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        ...containerSx,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 2,
          mb: 1,
        }}
      >
        {title ? (
          <Typography variant="h3" data-test="pyramid-diagram-title" sx={{ color: 'text.primary' }}>
            {title}
          </Typography>
        ) : (
          <Box />
        )}

        {isLoading ? (
          <Skeleton animation="wave" variant="text" width={180} data-test="pyramid-diagram-meta-loading" />
        ) : (
          metaStats &&
          metaStats.length > 0 && (
            <Box
              data-test="pyramid-diagram-meta"
              sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'flex-end' }}
            >
              {metaStats.map(stat => (
                <Typography key={stat.label} variant="body2" sx={{ color: 'text.secondary' }}>
                  {stat.label}: {stat.value}
                </Typography>
              ))}
            </Box>
          )
        )}
      </Box>

      {showChart || isLoading ? (
        <Box
          data-test="pyramid-diagram-legend"
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
            mb: 1,
          }}
        >
          {isLoading ? (
            <>
              <Skeleton animation="wave" variant="rectangular" width={120} height={20} />
              <Skeleton animation="wave" variant="rectangular" width={120} height={20} />
            </>
          ) : (
            <>
              <LegendItem
                label={left.label}
                totalPercent={left.totalPercent}
                color={leftColor}
                valueFormatter={valueFormatter}
              />
              <LegendItem
                label={right.label}
                totalPercent={right.totalPercent}
                color={rightColor}
                valueFormatter={valueFormatter}
              />
            </>
          )}
        </Box>
      ) : null}

      {showChart ? (
        <Box
          data-test="pyramid-diagram-chart"
          sx={{
            display: 'grid',
            gridTemplateColumns: `1fr ${CENTER_LABEL_WIDTH}px 1fr`,
            width: '100%',
            height,
            alignItems: 'stretch',
          }}
        >
          <SideChart
            side="left"
            data={chartData}
            dataKey="leftValue"
            name={left.label}
            color={leftColor}
            domainMax={domainMax}
            ticks={ticks}
            valueFormatter={valueFormatter}
            isAnimationActive={isAnimationActive}
            height={height}
            leftSeries={{ label: left.label, color: leftColor }}
            rightSeries={{ label: right.label, color: rightColor }}
          />

          <Box
            data-test="pyramid-diagram-axis-labels"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              height: labelsColumnHeight,
              pt: '4px',
            }}
          >
            {chartData.map(row => (
              <Box
                key={row.label}
                sx={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography variant="body1" sx={{ color: 'text.primary', textAlign: 'center', lineHeight: 1.2 }}>
                  {row.label}
                </Typography>
              </Box>
            ))}
          </Box>

          <SideChart
            side="right"
            data={chartData}
            dataKey="rightValue"
            name={right.label}
            color={rightColor}
            domainMax={domainMax}
            ticks={ticks}
            valueFormatter={valueFormatter}
            isAnimationActive={isAnimationActive}
            height={height}
            leftSeries={{ label: left.label, color: leftColor }}
            rightSeries={{ label: right.label, color: rightColor }}
          />
        </Box>
      ) : (
        <NoDataOrLoadingState isLoading={isLoading} height={height} />
      )}
    </Box>
  );
};
